import { axiosClient as axios } from './axiosClient';

import { BillingAddressListResultViewModel } from '@models/BillingAddress';
import {
  failureResult,
  ResultViewModel,
  successResult,
} from '@models/common/ResultViewModel';
import { ContactListResultViewModel } from '@models/Contact';
import {
  BillingAddressesSelectOptionResultViewModel,
  CustomerContactSelectOptionResultViewModel,
  CustomerResultViewModel,
  CustomerSelectOptionResultViewModel,
  CustomerTableResultViewModel,
  CustomerViewModel,
  ShippingAddressesSelectOptionResultViewModel,
} from '@models/Customer';
import { ShippingAddressListResultViewModel } from '@models/ShippingAddress';
import { handleAxiosError } from '@utils/axiosErrorHandler';
import { getDataUrl } from '@utils/dataUrls';
import { api } from 'src/config/apiEndpoints';

export const CustomerService = {
  create: async function (
    customer: CustomerViewModel
  ): Promise<CustomerResultViewModel | ResultViewModel> {
    const createUrl = `${api.BASE_URL}${api.CUSTOMERS_ENDPOINT}`;

    try {
      const response = await axios.post(createUrl, customer);
      return response.data as CustomerResultViewModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  delete: async function (id: number): Promise<ResultViewModel> {
    const deleteURL = `${api.BASE_URL}${api.CUSTOMERS_ENDPOINT}/${id}`;

    try {
      const response = await axios.delete(deleteURL);
      if (response.status === 204) {
        return successResult;
      }

      return failureResult;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  getList: async function (
    page?: number,
    pageSize?: number,
    searchFields?: string,
    search?: string,
    filters?: string,
    sortBy?: string,
    sortDirection?: string
  ): Promise<CustomerTableResultViewModel | ResultViewModel> {
    let dataUrl = getDataUrl(
      api.BASE_URL,
      api.CUSTOMERS_ENDPOINT,
      page,
      pageSize,
      searchFields,
      search,
      sortBy,
      sortDirection
    );

    if (filters) {
      dataUrl = dataUrl + filters;
    }

    try {
      const response = await axios.get(dataUrl);
      return response.data as CustomerTableResultViewModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  getById: async function (
    id: number
  ): Promise<CustomerResultViewModel | ResultViewModel> {
    const dataUrl = `${api.BASE_URL}${api.CUSTOMERS_ENDPOINT}/${id}`;

    try {
      const response = await axios.get(dataUrl);
      return response.data as CustomerResultViewModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  getCustomerContacts: async function (
    id: number
  ): Promise<ContactListResultViewModel | ResultViewModel> {
    const dataUrl = `${api.BASE_URL}${api.CUSTOMERS_ENDPOINT}/${id}/contacts`;

    try {
      const response = await axios.get(dataUrl);
      return response.data as ContactListResultViewModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  getCustomerBillingAddresses: async function (
    id: number
  ): Promise<BillingAddressListResultViewModel | ResultViewModel> {
    const dataUrl = `${api.BASE_URL}${api.CUSTOMERS_ENDPOINT}/${id}/billing-addresses`;

    try {
      const response = await axios.get(dataUrl);
      return response.data as BillingAddressListResultViewModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  getCustomerShippingAddresses: async function (
    id: number
  ): Promise<ShippingAddressListResultViewModel | ResultViewModel> {
    const dataUrl = `${api.BASE_URL}${api.CUSTOMERS_ENDPOINT}/${id}/shipping-addresses`;

    try {
      const response = await axios.get(dataUrl);
      return response.data as ShippingAddressListResultViewModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  getCustomerSelectOptions: async function (): Promise<
    CustomerSelectOptionResultViewModel | ResultViewModel
  > {
    const dataUrl = `${api.BASE_URL}${api.CUSTOMERS_ENDPOINT}/select-options`;

    try {
      const response = await axios.get(dataUrl);
      return response.data as CustomerSelectOptionResultViewModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  getCustomerContactsSelectOptions: async function (
    id: number
  ): Promise<CustomerContactSelectOptionResultViewModel | ResultViewModel> {
    const dataUrl = `${api.BASE_URL}${api.CUSTOMERS_ENDPOINT}/${id}/contacts/select-options`;

    try {
      const response = await axios.get(dataUrl);
      return response.data as CustomerContactSelectOptionResultViewModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  getCustomerBillingAddressesSelectOptions: async function (
    id: number
  ): Promise<BillingAddressesSelectOptionResultViewModel | ResultViewModel> {
    const dataUrl = `${api.BASE_URL}${api.CUSTOMERS_ENDPOINT}/${id}/billing-addresses/select-options`;

    try {
      const response = await axios.get(dataUrl);
      return response.data as BillingAddressesSelectOptionResultViewModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  getCustomerShippingAddressesSelectOptions: async function (
    id: number
  ): Promise<ShippingAddressesSelectOptionResultViewModel | ResultViewModel> {
    const dataUrl = `${api.BASE_URL}${api.CUSTOMERS_ENDPOINT}/${id}/shipping-addresses/select-options`;

    try {
      const response = await axios.get(dataUrl);
      return response.data as ShippingAddressesSelectOptionResultViewModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  update: async function (
    id: number,
    customer: CustomerViewModel
  ): Promise<ResultViewModel> {
    const updateUrl = `${api.BASE_URL}${api.CUSTOMERS_ENDPOINT}/${id}`;

    try {
      const response = await axios.put(updateUrl, customer);
      if (response.status === 204) {
        return successResult;
      }

      return failureResult;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
};
